import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { db } from '../../../../Config/firebase';
import { collection, deleteDoc, doc, query, onSnapshot, GeoPoint } from 'firebase/firestore';
import { useAppSelector } from '../../../../state/state';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import './Offers.css';
import { openModal } from '../../../../state/ModalSlice/ModalSlice';
import EditOfferModal from '../../../Common/Modals/EditOfferModal';
import { CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {  Box, Chip } from '@mui/material';
import AddOfferModal from '../../../Common/Modals/AddOfferModal';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import discountPana from "../../../../assets/GifsAndPlaceholders/Discount-pana.png"
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';
// Remove the SVG import

export enum OfferConstraint {
    WEEKDAY = "Weekday",
    WEEKEND = "Weekend",
    NON_HOLIDAYS = "Non Holidays",
    EVERYDAY = "Everyday"
}

export interface Offer {
    id: string;
    BuzzName: string;
    Description: string;
    OfferCode: string;
    isLive: boolean;
    url: string;
    offerConstraint: OfferConstraint;
    StartDate: firebase.firestore.Timestamp;
    EndDate: firebase.firestore.Timestamp;
    Price: number,
    placeName: string,
    placeAddress: string,
    geoLocation: {
        hash: string,
        latlang: GeoPoint
    }
}

const Offers: React.FC = () => {
    const [offers, setOffers] = useState<Offer[]>([]);
    const providerId: string = useAppSelector(selectEffectiveUID)  
    const dispatch = useDispatch();
    const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

    const [filters, setFilters] = useState({
        Live: true,
        NotLive: true,
    });

    const [isDeletingOffer, setIsDeletingOffer] = useState<string | null>(null);

    // Reference to store the unsubscribe function
    const unsubscribeRef = useRef<() => void | undefined>();

    useEffect(() => {
        if (providerId) {
            const offersRef = collection(db, 'ServiceProviders', providerId, 'Offers');
            const q = query(offersRef);

            const unsubscribe = onSnapshot(q, (snapshot) => {
                const offersList: Offer[] = [];
                snapshot.forEach((doc) => {
                    offersList.push({ id: doc.id, ...doc.data() } as Offer);
                });
                setOffers(offersList);
            }, (error) => {
                console.error("Error fetching offers: ", error);
            });

            // Store the unsubscribe function
            unsubscribeRef.current = unsubscribe;

            // Cleanup function
            return () => {
                if (unsubscribeRef.current) {
                    unsubscribeRef.current();
                }
            };
        }
    }, [providerId]);

    const handleFilterChange = (filter: keyof typeof filters) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filter]: !prevFilters[filter],
        }));
    };

    const handleAddOffer = () => {
        dispatch(openModal('AddOfferModalOpen'));
    };

    const handleEditOffer = (offerId: string) => {
        setSelectedOfferId(offerId);
        dispatch(openModal('EditOfferModalOpen'));
    };

    const handleDeleteOffer = async (offerId: string) => {
        if (!providerId) {
            console.error('Provider ID not found');
            return;
        }

        setIsDeletingOffer(offerId);
        try {
            const offerRef = doc(db, 'ServiceProviders', providerId, 'Offers', offerId);
            await deleteDoc(offerRef);
            // No need to update local state here as the listener will handle it
        } catch (error) {
            console.error('Error deleting offer:', error);
            // Optionally, show an error message to the user
        } finally {
            setIsDeletingOffer(null);
        }
    };

    // const isEditOfferModalOpen = useSelector((state: any) => state.ModalSlice.EditOfferModalOpen);
    const isAddOfferModalOpen = useSelector((state: any) => state.ModalSlice.AddOfferModalOpen);

    const filteredOffers = offers.filter(offer =>
        (offer.isLive && filters.Live) || (!offer.isLive && filters.NotLive)
    );



    // Add this import for the default image

    const getChipColor = (constraint: OfferConstraint) => {
        switch (constraint) {
            case OfferConstraint.EVERYDAY:
                return 'bg-purple-500';
            case OfferConstraint.WEEKDAY:
                return 'bg-blue-500';
            case OfferConstraint.WEEKEND:
                return 'bg-orange-500';
            case OfferConstraint.NON_HOLIDAYS:
                return 'bg-green-500';
            default:
                return 'bg-gray-500';
        }
    };

    return (
        <>
            <div className="p-4 mt-4">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <div>
                        <p className='text-2xl font-semibold'>Your Offers</p>
                        <p className='mt-1'>Create and manage your offers to attract more customers and boost sales.</p>
                    </div>

                    <button onClick={handleAddOffer}

                        className={`w-full max-w-40 bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center  cursor-pointer`}>
                        <AddIcon />
                        <p className={"ml-2"}> Add Offers</p>
                    </button>


                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 4 }}>
                    <Chip
                        label="Live Offers"
                        onClick={() => handleFilterChange('Live')}
                        color={filters.Live ? "primary" : "default"}
                        sx={{
                            backgroundColor: filters.Live ? '#1D4ED8' : '#E0E0E0',
                        }}
                    />
                    <Chip
                        label="Dormant Offers"
                        onClick={() => handleFilterChange('NotLive')}
                        color={filters.NotLive ? "primary" : "default"}
                        sx={{
                            backgroundColor: filters.NotLive ? '#1D4ED8' : '#E0E0E0',
                        }}
                    />
                </Box>

                <div className="flex overflow-x-auto pb-4 diff-scrollbar">
                    <ul className="flex gap-4">
                        {filteredOffers.map((offer) => (
                            <li key={offer.id} className="min-w-[340px] max-w-[340px]">
                                <article className="h-full rounded-xl bg-white border border-gray-300 p-3 shadow-lg hover:shadow-xl">
                                    <div className="relative flex items-end overflow-hidden rounded-xl">
                                        <div className="relative w-full h-48">
                                            <img
                                                src={offer.url || discountPana}
                                                alt={offer.BuzzName}
                                                className="object-cover w-full h-full transition-transform duration-300 transform hover:scale-105"
                                            />
                                        </div>
                                        <div className={`absolute top-3 right-3 inline-flex items-center rounded-xl ${offer.isLive ? "bg-green-600" : "bg-red-600"} px-2.5 shadow-2xl py-1`}>
                                            <span className="text-white ml-1 text-sm font-semibold">{offer.isLive ? "Live" : "Expired"}</span>
                                        </div>
                                    </div>

                                    <div className="mt-1 p-2 flex flex-col h-[calc(100%-12rem)]">
                                        <div className="mb-2">
                                            <h2 className="text-slate-800 font-bold text-lg line-clamp-2 tracking-tight">
                                                {offer.BuzzName}
                                            </h2>
                                        </div>
                                        <div className="mb-3">
                                            <p className="text-gray-800 text-sm line-clamp-2 italic">
                                                {offer.Description}
                                            </p>
                                        </div>

                                        <div className="space-y-1.5 flex-grow">
                                            <p className="text-slate-700 text-sm">Offer Applicable on - <span className='font-semibold text-slate-900'>{offer.offerConstraint}</span></p>
                                            <p className='text-slate-700 text-sm'>Price - <span className='font-semibold text-slate-900'> {offer.Price ? `₹${offer.Price.toString()}` : "FREE"}</span></p>
                                            <p className="text-slate-700 text-sm">Offer Code - <span className='font-semibold text-slate-900'>{offer.OfferCode}</span></p>

                                            <p className='text-slate-700 text-sm'>Address - <span className='font-semibold text-slate-900 line-clamp-1'>{offer.placeAddress ? offer.placeAddress : ""}</span></p>
                                            <p className='text-slate-700 text-sm'>Validity - <span className='font-semibold text-slate-900 '>
                                                {offer.StartDate && offer.EndDate 
                                                    ? `${new Date(offer.StartDate.seconds * 1000).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric'
                                                      })} - ${new Date(offer.EndDate.seconds * 1000).toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric'
                                                      })}` 
                                                    : 'Not specified'}
                                            </span></p>
                                        </div>

                                        <div className="mt-3">
                                            <button
                                                onClick={() => handleEditOffer(offer.id)}
                                                className="w-full bg-blue-100 hover:bg-blue-200 text-blue-600 py-2 px-3 rounded-lg transition-all duration-200"
                                            >
                                                <EditIcon fontSize='small' sx={{ width: 20, height: 20 }} className="w-4 h-4 mr-1 inline-block" /> Edit
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            {selectedOfferId && <EditOfferModal offerId={selectedOfferId} />}
            {isAddOfferModalOpen && <AddOfferModal />}
        </>
    );
};

export default Offers;
